/**
 * 登录/注册页面组件
 *
 * 功能:
 * - 提供登录和注册表单切换
 * - 验证用户输入
 * - 通过REST API与后端通信
 * - 使用 HttpOnly Cookie 存储认证状态
 * - 登录/注册成功后根据来源页面跳转
 * 
 * @version 2.0.0 - 修复 React Hooks 规则违反问题
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRateLimit } from '../hooks/useRateLimit';
import { usePasswordVisibility } from '../hooks/usePasswordVisibility';
import { useAuthStore } from '../store/authStore';
import { authApi, toUser, AuthApiError, ValidationError, ConflictError, UnauthorizedError } from '../services/authApi';
import { PasswordInput } from '../components/PasswordInput';
import { SliderCaptcha } from '../components/SliderCaptcha';
import type { SubscriptionType } from '../types/auth';

type FormMode = 'login' | 'register';
type AuthMethod = 'password' | 'sms';

// 默认跳转路径配置
const DEFAULT_REDIRECTS = {
  author: '/author-center',
  book: '/book',
} as const;

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkRateLimit, getRemainingTime } = useRateLimit();
  const { showPassword, showPasswordConfirm, togglePassword, togglePasswordConfirm } =
    usePasswordVisibility();

  // 认证状态
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const initialized = useAuthStore((state) => state.initialized);

  const [mode, setMode] = useState<FormMode>('login');
  const [authMethod, setAuthMethod] = useState<AuthMethod>('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 短信验证码状态
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsSending, setSmsSending] = useState(false);
  const smsPendingRef = useRef(false); // 待滑块验证后发送短信

  // 表单状态
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nickname, setNickname] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // 获取来源页面路径
  const from = useMemo(() => {
    const state = location.state as { from?: { pathname: string } } | null;
    if (state?.from?.pathname) {
      return state.from.pathname;
    }
    // 如果没有来源信息，默认跳转到作者中心
    return DEFAULT_REDIRECTS.author;
  }, [location.state]);

  /**
   * 处理认证成功
   */
  const handleAuthSuccess = useCallback((response: {
    user: {
      id: number;
      email: string;
      nickname: string | null;
      level: string;
      subscription_type: SubscriptionType;
      is_verified: boolean;
      created_at: string;
      last_login_at: string | null;
    };
    access_token?: string;
  }) => {
    const user = toUser(response.user);
    const token = response.access_token || null;

    useAuthStore.getState().setUser(user);
    if (token) {
      useAuthStore.getState().setToken(token);
    }

    useAuthStore.setState({
      isAuthenticated: true,
      initialized: true,
      loading: false,
    });

    navigate(from, { replace: true });
  }, [navigate, from]);

  const handleRegisterSuccess = useCallback((response: {
    message: string;
    email: string;
    requires_verification: boolean;
  }) => {
    navigate(`/verify-email?email=${encodeURIComponent(response.email)}`, { replace: true });
  }, [navigate]);

  const handleCaptchaSuccess = useCallback((token: string) => {
    setCaptchaToken(token);
    setShowCaptcha(false);
    // 若是短信发送流程触发的滑块，拿到 token 后立即发送
    if (smsPendingRef.current) {
      smsPendingRef.current = false;
      // 延迟一帧让状态先落地
      setTimeout(() => sendSmsCodeRef.current?.(token), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 获取友好的错误消息
   */
  const getErrorMessage = useCallback((err: unknown, mode: FormMode): string => {
    if (err instanceof ValidationError) {
      return err.message;
    }
    if (err instanceof ConflictError) {
      if (err.message.includes('邮箱')) {
        return '该邮箱已被注册';
      }
      if (err.message.includes('昵称')) {
        return '该昵称已被使用';
      }
      return err.message;
    }
    if (err instanceof UnauthorizedError) {
      return mode === 'login' ? '邮箱或密码错误' : err.message;
    }
    if (err instanceof AuthApiError) {
      return err.message;
    }
    if (err instanceof Error) {
      // 网络错误
      if (err.message.includes('网络') || err.message.includes('fetch')) {
        return '网络连接失败，请检查网络设置';
      }
      return err.message;
    }
    return '操作失败，请稍后重试';
  }, []);

  /**
   * 发送短信验证码（需先通过滑块）
   */
  const sendSmsCode = useCallback(async (token?: string) => {
    setError('');
    const tokenToUse = token ?? captchaToken;
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的中国大陆手机号');
      return;
    }
    if (!tokenToUse) {
      smsPendingRef.current = true;
      setShowCaptcha(true);
      return;
    }
    setSmsSending(true);
    try {
      const resp = await authApi.smsSend(phone, tokenToUse);
      setCaptchaToken(null); // 一次性 token 用完即弃
      const interval = resp.interval_seconds || 60;
      setSmsCountdown(interval);
      const timer = setInterval(() => {
        setSmsCountdown((c) => {
          if (c <= 1) {
            clearInterval(timer);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } catch (err) {
      setCaptchaToken(null);
      setError(getErrorMessage(err, 'login'));
    } finally {
      setSmsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, captchaToken, getErrorMessage]);

  // 供滑块回调异步调用的最新引用（避免闭包/声明顺序问题）
  const sendSmsCodeRef = useRef<((token?: string) => void) | null>(null);
  useEffect(() => {
    sendSmsCodeRef.current = sendSmsCode;
  }, [sendSmsCode]);


  /**
   * 处理表单提交
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setLoading(true);

      // 表单验证（短信登录分支不校验邮箱/密码，在下方单独处理）
      if (authMethod !== 'sms' && (!email || !password)) {
        setError('请填写邮箱和密码');
        setLoading(false);
        return;
      }

      if (authMethod !== 'sms' && mode === 'register') {
        if (password !== passwordConfirm) {
          setError('两次输入的密码不一致');
          setLoading(false);
          return;
        }
        if (password.length <= 8) {
          setError('密码必须大于8位');
          setLoading(false);
          return;
        }
        if (!/^(?=.*[a-zA-Z])(?=.*\d)/.test(password)) {
          setError('密码必须包含字母和数字');
          setLoading(false);
          return;
        }
        if (!agreedToTerms) {
          setError('请阅读并同意用户协议和隐私政策');
          setLoading(false);
          return;
        }
      }

      // 限流检查
      if (!checkRateLimit()) {
        setError(`请等待 ${getRemainingTime()} 秒后再试`);
        setLoading(false);
        return;
      }

      // 手机号验证码登录分支
      if (authMethod === 'sms') {
        if (!/^1[3-9]\d{9}$/.test(phone)) {
          setError('请输入正确的中国大陆手机号');
          setLoading(false);
          return;
        }
        if (!smsCode) {
          setError('请输入短信验证码');
          setLoading(false);
          return;
        }
        try {
          const response = await authApi.smsLogin(phone, smsCode);
          handleAuthSuccess(response);
        } catch (err) {
          setError(getErrorMessage(err, 'login'));
        } finally {
          setLoading(false);
        }
        return;
      }

      try {
        let response;
      if (mode === 'login') {
        response = await authApi.login(email, password);
        handleAuthSuccess(response);
      } else {
        if (!captchaToken) {
          setShowCaptcha(true);
          setLoading(false);
          return;
        }
        response = await authApi.register(email, password, passwordConfirm, nickname || undefined, captchaToken);
        setCaptchaToken(null);
        if (response.requires_verification) {
          handleRegisterSuccess(response);
        } else {
          handleAuthSuccess(response as any);
        }
      }
      } catch (err) {
        setError(getErrorMessage(err, mode));
      } finally {
        setLoading(false);
      }
    },
    [email, password, passwordConfirm, nickname, mode, agreedToTerms, captchaToken, checkRateLimit, getRemainingTime, handleAuthSuccess, handleRegisterSuccess, getErrorMessage, authMethod, phone, smsCode]
  );

  /**
   * 切换表单模式
   */
  const toggleMode = useCallback(() => {
    setError('');
    setCaptchaToken(null);
    setShowCaptcha(false);
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
  }, []);

  // UI文本
  const uiText = useMemo(
    () => ({
      title: mode === 'login' ? '登录' : '注册',
      subtitle: mode === 'login' ? '欢迎回来' : '创建您的账号',
      buttonText: loading
        ? mode === 'login'
          ? '登录中...'
          : '注册中...'
        : mode === 'login'
        ? '登录'
        : '注册',
      togglePrefix: mode === 'login' ? '还没有账号？' : '已有账号？',
      toggleAction: mode === 'login' ? '立即注册' : '立即登录',
      passwordPlaceholder:
        mode === 'login'
          ? '请输入密码'
          : '大于8位,包含字母和数字',
    }),
    [mode, loading]
  );

  // 已登录用户重定向
  useEffect(() => {
    if (initialized && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [initialized, isAuthenticated, navigate, from]);

  // 等待初始化完成
  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  // 已登录用户不显示登录页
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-paper-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* 标题 */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-ink-800 mb-2">{uiText.title}</h1>
          <p className="text-ink-600">{uiText.subtitle}</p>
        </div>

        {/* 表单卡片 */}
        <div className="bg-white rounded-lg shadow-md p-8 border border-paper-200">
          {/* 错误提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* 登录方式切换（仅登录模式显示手机验证码选项） */}
          <div className="mb-6 flex border-b border-paper-200">
            <button
              type="button"
              onClick={() => { setAuthMethod('password'); setError(''); }}
              className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
                authMethod === 'password'
                  ? 'border-ink-800 text-ink-800'
                  : 'border-transparent text-ink-400 hover:text-ink-600'
              }`}
              disabled={loading}
            >
              账号密码
            </button>
            <button
              type="button"
              onClick={() => { setAuthMethod('sms'); setError(''); }}
              className={`flex-1 pb-2 text-sm font-medium border-b-2 transition-colors ${
                authMethod === 'sms'
                  ? 'border-ink-800 text-ink-800'
                  : 'border-transparent text-ink-400 hover:text-ink-600'
              }`}
              disabled={loading}
            >
              手机验证码
            </button>
          </div>

          {authMethod === 'sms' ? (
            <>
              {/* 手机号输入框 */}
              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-ink-800 mb-2">
                  手机号
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  className="w-full px-4 py-2 border border-paper-200 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
                  placeholder="请输入中国大陆手机号"
                  disabled={loading || smsSending}
                  maxLength={11}
                />
              </div>

              {/* 短信验证码输入 + 发送按钮 */}
              <div className="mb-4">
                <label htmlFor="sms-code" className="block text-sm font-medium text-ink-800 mb-2">
                  短信验证码
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="sms-code"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="flex-1 px-4 py-2 border border-paper-200 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
                    placeholder="6位验证码"
                    disabled={loading}
                    maxLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => sendSmsCode()}
                    disabled={smsCountdown > 0 || smsSending || loading || !phone}
                    className="px-4 py-2 text-sm whitespace-nowrap border border-ink-800 text-ink-800 rounded-md hover:bg-ink-800 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-ink-800 transition-all"
                  >
                    {smsSending
                      ? '发送中...'
                      : smsCountdown > 0
                      ? `${smsCountdown}s`
                      : '获取验证码'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-ink-400">
                  未注册的手机号验证后将自动创建账号，即表示同意
                  <a href="/user-agreement.html" target="_blank" rel="noopener noreferrer" className="text-paper-600 hover:text-paper-700 underline mx-0.5">用户协议</a>和
                  <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-paper-600 hover:text-paper-700 underline mx-0.5">隐私政策</a>
                </p>
              </div>

              {/* 提交按钮（短信登录） */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-ink-800 hover:bg-ink-700 disabled:bg-ink-400 text-white px-6 py-3 rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150"
              >
                {loading ? '登录中...' : '登录 / 注册'}
              </button>
            </>
          ) : (
            <>
          {/* 邮箱输入框 */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-ink-800 mb-2">
                邮箱
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-paper-200 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
                placeholder="请输入邮箱"
                disabled={loading}
              />
            </div>

            {/* 密码输入框 */}
            <PasswordInput
              id="password"
              label="密码"
              value={password}
              onChange={setPassword}
              showPassword={showPassword}
              onToggleVisibility={togglePassword}
              placeholder={uiText.passwordPlaceholder}
              disabled={loading}
            />

            {/* 密码确认输入框（仅注册模式） */}
            {mode === 'register' && (
              <PasswordInput
                id="password-confirm"
                label="确认密码"
                value={passwordConfirm}
                onChange={setPasswordConfirm}
                showPassword={showPasswordConfirm}
                onToggleVisibility={togglePasswordConfirm}
                placeholder="请再次输入密码"
                disabled={loading}
              />
            )}

            {/* 昵称输入框（仅注册模式） */}
            {mode === 'register' && (
              <div className="mb-4">
                <label htmlFor="nickname" className="block text-sm font-medium text-ink-800 mb-2">
                  昵称
                </label>
                <input
                  type="text"
                  id="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  className="w-full px-4 py-2 border border-paper-200 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
                  placeholder="2-20个字符，支持中英文、数字和下划线"
                  disabled={loading}
                  maxLength={20}
                />
              </div>
            )}

            {/* 用户协议和隐私政策勾选框（仅注册模式） */}
            {mode === 'register' && (
              <div className="mb-4">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-paper-500 border-gray-300 rounded focus:ring-paper-500"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-ink-600">
                    我已阅读并同意
                    <a
                      href="/user-agreement.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-paper-600 hover:text-paper-700 underline"
                    >
                      用户协议
                    </a>
                    和
                    <a
                      href="/privacy-policy.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-paper-600 hover:text-paper-700 underline"
                    >
                      隐私政策
                    </a>
                  </span>
                </label>
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-ink-800 hover:bg-ink-700 disabled:bg-ink-400 text-white px-6 py-3 rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:hover:translate-y-0"
            >
              {uiText.buttonText}
            </button>

            {/* 切换模式链接 */}
            <div className="mt-4 text-center">
              <span className="text-sm text-ink-600">{uiText.togglePrefix}</span>
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-ink-600 hover:text-ink-800 font-medium ml-1"
                disabled={loading}
              >
                {uiText.toggleAction}
              </button>
            </div>
            </>
          )}
          </form>

          {captchaToken && mode === 'register' && authMethod === 'password' && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-green-600">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8.5L6.5 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>安全验证已完成</span>
            </div>
          )}
        </div>
      </div>

      {showCaptcha && (
        <SliderCaptcha
          onSuccess={handleCaptchaSuccess}
          onClose={() => setShowCaptcha(false)}
        />
      )}
    </div>
  );
};

export default LoginPage;